import 'twin.macro';
import { useCountryConfig } from 'queries/user/useCountryConfig';
import React, { useState } from 'react';
import useEleves from '@teetsh/app/src/queries/eleves/useEleves';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import usePyramide from '@teetsh/app/src/queries/pyramides/usePyramide';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { Pyramide, PyramidePDFSettings } from '@teetsh/app/src/types/pyramide';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@teetsh/app/src/components/Resizable';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import RedirectSchoolSchoolyear from '@teetsh/app/src/components/Routing/RedirectSchoolSchoolyear';
import { EmptyModule } from 'components/EmptyModule';
import { PlusIcon as Plus } from '@heroicons/react/24/outline';
import SchoolyearButtonLink from 'components/Buttons/SchoolyearButtonLink';
import { screenshotUrls } from 'containers/screenshotUrls';
import { isEleveInDecloisonnement } from 'utils/isEleveInDecloisonnement';
import { isEleveNotInDecloisonnement } from 'utils/isEleveNotInDecloisonnement';
import { useTranslation } from 'react-i18next';
import { Infobox } from 'components/Callout/Infobox';
import AutoSubmitForm from 'components/AutoSave';
import { Sidebar } from './Sidebar';
import { Form } from 'react-final-form';
import useUpdateUser from 'queries/user/useUpdateUser';
import { useUserAccount } from 'queries/user/account/useUserAccount';
import useDownloadPyramide from 'queries/pyramides/useDownloadPyramide';
import { Body } from './Body';
import { useUpdatePyramideDebounce } from 'queries/pyramides/useUpdatePyramideDebounce';
import { getDistinctEleveGroups } from 'utils/getDistinctEleveGroups';

interface Props {
  eleves: Eleve[];
  elevesEnDecloisonnement: Eleve[];
  pyramide: Pyramide;
}

export function PyramideRoot(props: Props) {
  const { t } = useTranslation(['eleve']);
  const updateUser = useUpdateUser();
  const user = useUser();
  const { eleves, elevesEnDecloisonnement } = props;
  const { isIllustrationEnabled } = useCountryConfig();

  const [pyramide, updatePyramide] = useUpdatePyramideDebounce(props.pyramide);
  const elevesGroupes = getDistinctEleveGroups(eleves);
  const [selectedGroups, setSelectedGroups] = useState(elevesGroupes);
  const { data: userAccount } = useUserAccount();
  const { download, isDownloading } = useDownloadPyramide();
  const groupsToDisplay = elevesGroupes.every(eg => selectedGroups.includes(eg))
    ? undefined
    : selectedGroups;

  const downloadPyramide = (settings: PyramidePDFSettings) =>
    download({
      settings,
      pyramide: {
        ...pyramide,
        title: t(`eleve:pyramide.labels.${pyramide.title}`, {
          defaultValue: pyramide.title,
        }),
      },
      selectedGroups: groupsToDisplay,
    });
  if (eleves.length === 0) {
    return (
      <EmptyModule
        title={t('eleve:pyramide.welcomeTitle')}
        subtitle={t('eleve:pyramide.welcomeSubtitle')}
        imageUrl={
          isIllustrationEnabled ? screenshotUrls.pyramideDesAges : undefined
        }
        cta={
          <SchoolyearButtonLink
            intent="primary"
            extralarge
            to="eleves/import?redirect=pyramides-des-ages"
            icon={Plus}
          >
            {t('eleve:pyramide.createButtonLabel')}
          </SchoolyearButtonLink>
        }
      />
    );
  }

  return (
    <Form<PyramidePDFSettings>
      initialValues={user.fonts.pyramide}
      onSubmit={async values => {
        await updateUser({
          ...user,
          fonts: {
            ...user.fonts,
            pyramide: values,
          },
        });
        return {};
      }}
    >
      {({ handleSubmit }) => (
        <form tw="min-h-0 h-full" onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="w-full h-full flex flex-col overflow-hidden">
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="eleves-preview-sidebar"
            >
              <Sidebar
                onDownload={downloadPyramide}
                isDownloading={isDownloading}
                onUpdatePyramide={updatePyramide}
                pyramide={pyramide}
                groups={elevesGroupes}
                selectedGroups={selectedGroups}
                setSelectedGroups={setSelectedGroups}
                limitedBgSet={userAccount?.isOnOldPricing || false}
              />
              <ResizableHandle />
              <ResizablePanel order={1}>
                <div tw="h-full overflow-auto">
                  {elevesEnDecloisonnement.length > 0 && (
                    <Infobox
                      intent="warning"
                      tw="mb-4"
                      title={t('eleve:pyramide.xStudentDecloisonnement', {
                        count: elevesEnDecloisonnement.length,
                      })}
                    >
                      {t('eleve:pyramide.decloisonnementHint')}
                    </Infobox>
                  )}

                  <Body
                    groups={groupsToDisplay}
                    pyramide={pyramide}
                    lng={user.locale}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </form>
      )}
    </Form>
  );
}

export default function PyramideData() {
  const { access } = useUser();
  const { data, isLoading, isSuccess } = useEleves();
  const { data: pyramide } = usePyramide();
  if (!access.hasAccessToPyramide) {
    return <RedirectSchoolSchoolyear to="pyramides-des-ages/locked" />;
  }
  if (isLoading || !isSuccess || !pyramide) {
    return <SpinnerComponent />;
  }
  const elevesNotInDecloisonnement = (data || []).filter(ev =>
    isEleveNotInDecloisonnement(ev),
  );
  const elevesInDecloisonnement = (data || []).filter(ev =>
    isEleveInDecloisonnement(ev),
  );
  return (
    <PyramideRoot
      pyramide={pyramide}
      eleves={elevesNotInDecloisonnement}
      elevesEnDecloisonnement={elevesInDecloisonnement}
    />
  );
}
